// Layer 3 (Transformation): cost per tonne — the rate card, the three category
// variance cards, and the period's cost build-up widened with its Budget and
// Variance columns.
//
// The Budget rate is a *rate* basis, not a volume budget: it divides the
// period's budgeted cost at BUDGET_TONNES_PER_DAY by the tonnes actually
// milled in the period — the same actual-tonnes denominator the shipped
// margin/t Budget already uses, kept deliberately so one Budget cannot mean
// two different things on one route.
//
// Labour carries no variance card: it is a fixed line at the same rate in
// both actual and budget at every period, so its variance is identically $0
// and a card would be a card that never moves.

import { formatMoney, formatMoneyPerDst } from '@/versions/v1/shared/NsrEngine';
import {
  budgetCostCategoriesForDays,
  costPeriod,
  daysForPeriod,
  formatSignedMoney,
  type CostCategory,
  type CostCategoryName,
  type CostPeriod,
  type CostPeriodView,
} from './Scene.transform';
import { BUDGET_TONNES_PER_DAY, COST_SEED_PROVENANCE } from './seed';
import type { CostKpiCard } from './Scene.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

/** The three categories whose actual-versus-Budget variance carries meaning.
 *  Ordered by the size of the exposure they describe, not alphabetically. */
export const VARIANCE_CARD_CATEGORIES: readonly CostCategoryName[] = ['Reagents', 'Power', 'Maintenance'];

export type CostBuildUpRow = {
  category: CostCategoryName;
  lines: CostCategory['lines'];
  budget: number;
  actual: number;
  variance: number;
};

export type CategoryVariance = {
  category: CostCategoryName;
  variance: number;
  value: string;
  tone: 'ok' | 'warn';
};

export type CostPerTonneView = {
  period: CostPeriod;
  label: string;
  dateRange: string;
  dayCount: number;
  tonnesMilled: number;
  actualPerTonne: number;
  budgetPerTonne: number;
  /** One number reached three ways — the P&L total, this build-up's total and
   *  `costPeriod(period).costTotal` are the same figure. */
  buildUpTotal: number;
  buildUpBudget: number;
  cards: readonly CostKpiCard[];
  buildUp: readonly CostBuildUpRow[];
  variances: readonly CategoryVariance[];
  /** The reagent variance is extra tonnage at the budget rate, not a miss. */
  reagentVarianceVolumeDriven: boolean;
  provenance: string;
};

function sumLines(lines: readonly { dollars: number }[]): number {
  return lines.reduce((sum, line) => sum + line.dollars, 0);
}

export function costPerTonneView(period: CostPeriod): CostPerTonneView {
  const view: CostPeriodView = costPeriod(period);
  const days = daysForPeriod(period);
  const budgetCategories = budgetCostCategoriesForDays(days.length);

  const budgetTotal = budgetCategories.reduce((sum, category) => sum + category.subtotal, 0);
  const budgetPerTonne = view.tonnesMilled > 0 ? budgetTotal / view.tonnesMilled : 0;
  const actualPerTonne = view.tonnesMilled > 0 ? view.costTotal / view.tonnesMilled : 0;

  const buildUp: CostBuildUpRow[] = view.costCategories.map((category) => {
    const budgetCategory = budgetCategories.find((c) => c.category === category.category)!;
    return {
      category: category.category,
      lines: category.lines,
      budget: budgetCategory.subtotal,
      actual: category.subtotal,
      variance: category.subtotal - budgetCategory.subtotal,
    };
  });

  const variances: CategoryVariance[] = VARIANCE_CARD_CATEGORIES.map((name) => {
    const row = buildUp.find((r) => r.category === name)!;
    return {
      category: name,
      variance: row.variance,
      value: formatSignedMoney(row.variance),
      tone: row.variance <= 0 ? 'ok' : 'warn',
    };
  });

  // Reagent spend is the extra tonnage at the budget rate, not a rate overrun:
  // the same `reagentVarianceVolumeDriven` test the P&L row tone reads. The
  // card names the volume driver and the rate, and reads neutral.
  const budgetTonnes = days.length * BUDGET_TONNES_PER_DAY;
  const reagentRatePerSt = budgetTonnes > 0 ? buildUp.find((r) => r.category === 'Reagents')!.budget / budgetTonnes : 0;
  const tonneDelta = view.tonnesMilled - budgetTonnes;

  const cards: CostKpiCard[] = [
    {
      label: 'Cost / dst',
      value: formatMoneyPerDst(actualPerTonne),
      sub: `Budget ${formatMoneyPerDst(budgetPerTonne)} at actual tonnes`,
      tone: actualPerTonne <= budgetPerTonne ? 'ok' : 'warn',
    },
    ...variances.map((variance) => {
      const volumeDriven = view.reagentVarianceVolumeDriven && variance.category === 'Reagents';
      return {
        label: `${variance.category} vs Budget`,
        value: variance.value,
        sub: volumeDriven
          ? `Volume-driven · ${tonneDelta >= 0 ? '+' : '−'}${Math.abs(tonneDelta).toLocaleString('en-US')} dst · ${CURRENCY_GLYPH}${reagentRatePerSt.toFixed(2)}/dst actual = budget rate`
          : variance.variance <= 0
            ? 'Under budget'
            : 'Over budget',
        tone: volumeDriven ? ('neutral' as const) : variance.tone,
      };
    }),
  ];

  return {
    period,
    label: view.label,
    dateRange: view.dateRange,
    dayCount: days.length,
    tonnesMilled: view.tonnesMilled,
    actualPerTonne,
    budgetPerTonne,
    buildUpTotal: sumLines(view.costCategories.flatMap((category) => category.lines)),
    buildUpBudget: budgetTotal,
    cards,
    buildUp,
    variances,
    reagentVarianceVolumeDriven: view.reagentVarianceVolumeDriven,
    provenance: COST_SEED_PROVENANCE,
  };
}

export function formatVariance(n: number): string {
  // Zero is `$0`, the P&L grid's zero rule; a sign on nothing is a second glyph for one meaning.
  return n === 0 ? formatMoney(0) : formatSignedMoney(n);
}

export function formatBudgetCell(n: number): string {
  return formatMoney(n);
}

export type CostBuildUpLineCells = {
  label: string;
  qty: string;
  rate: string;
  actual: string;
  perDst: string;
};

export type CostBuildUpCategoryCells = {
  category: CostCategoryName;
  /** `7 lines`, with the volume-driven reagent note appended when it applies. */
  count: string;
  budget: string;
  actual: string;
  variance: string;
  /** `zero` prints muted; `neutral` is the volume-driven reagent variance. */
  varianceTone: 'ok' | 'warn' | 'neutral' | 'zero';
  perDst: string;
  lines: readonly CostBuildUpLineCells[];
};

export type CostBuildUpTable = {
  /** The Cost / dst figure split for the card's figure row. */
  figure: string;
  unit: string;
  /** `US$43.23/dst at actual tonnes`: the Budget rate the figure reads against. */
  budgetComparison: string;
  tonnesMilled: string;
  categories: readonly CostBuildUpCategoryCells[];
  total: { budget: string; actual: string; variance: string; varianceTone: 'ok' | 'bad'; perDst: string };
};

/** The Cost build-up card (polish.P7): the Cost / dst figure over the five
 *  categories, each a collapsible row whose lines carry quantity, rate, actual
 *  and US$/dst. Every figure is `costPerTonneView`'s; this only formats. The
 *  category note is the reagent card's volume-driven caption, lower-cased
 *  where it follows the line count. */
export function costBuildUpTable(period: CostPeriod): CostBuildUpTable {
  const view = costPerTonneView(period);
  const head = view.cards[0]!;
  const slash = head.value.indexOf('/');
  const reagentCard = view.cards.find((card) => card.label === 'Reagents vs Budget');
  const reagentNote = view.reagentVarianceVolumeDriven && reagentCard ? reagentCard.sub : null;
  const perDst = (dollars: number) => formatMoneyPerDst(view.tonnesMilled > 0 ? dollars / view.tonnesMilled : 0);
  const totalVariance = view.buildUpTotal - view.buildUpBudget;

  return {
    figure: slash > 0 ? head.value.slice(0, slash) : head.value,
    unit: slash > 0 ? head.value.slice(slash) : '',
    budgetComparison: head.sub.replace(/^Budget /, ''),
    tonnesMilled: `${view.tonnesMilled.toLocaleString('en-US')} dst milled`,
    categories: view.buildUp.map((row) => {
      const volumeDriven = reagentNote !== null && row.category === 'Reagents';
      const count = `${row.lines.length} line${row.lines.length === 1 ? '' : 's'}`;
      return {
        category: row.category,
        count: volumeDriven ? `${count} · ${reagentNote!.charAt(0).toLowerCase()}${reagentNote!.slice(1)}` : count,
        budget: formatBudgetCell(row.budget),
        actual: formatBudgetCell(row.actual),
        variance: formatVariance(row.variance),
        varianceTone: row.variance === 0 ? 'zero' : volumeDriven ? 'neutral' : row.variance < 0 ? 'ok' : 'warn',
        perDst: perDst(row.actual),
        lines: row.lines.map((line) => ({
          label: line.label,
          qty: `${line.qty.toLocaleString('en-US', { maximumFractionDigits: 1 })} ${line.qtyUnit}`,
          rate: `${line.rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${line.rateUnit}`,
          actual: formatBudgetCell(line.dollars),
          perDst: formatMoneyPerDst(line.dollarsPerT),
        })),
      };
    }),
    total: {
      budget: formatBudgetCell(view.buildUpBudget),
      actual: formatBudgetCell(view.buildUpTotal),
      variance: formatVariance(totalVariance),
      varianceTone: totalVariance <= 0 ? 'ok' : 'bad',
      perDst: formatMoneyPerDst(view.actualPerTonne),
    },
  };
}

/** The build-up's per-tonne column heading: the unit comes from the transform
 *  layer like every money string on this route. */
export const PER_DST_HEADING = `${CURRENCY_GLYPH}/dst`;
